import { z } from "zod";
import {
  Task,
  TaskType,
  TaskTypeSchema,
  TaskStatusSchema,
  CreateTaskDto,
  UpdateTaskDto,
} from "@myapp/contract";

export const taskStatusOptions = [
  { value: "PLANNED", label: "Geplant" },
  { value: "COMPLETED", label: "Abgeschlossen" },
  { value: "DELAYED", label: "Verzögert" },
  { value: "CANCELLED", label: "Storniert" },
];

export const taskFormSchema = z
  .object({
    title: z.string().min(1, "Pflichtfeld"),
    description: z.string().default(""),
    assignedTechnicianId: z.number().int().positive().nullable(),
    taskType: TaskTypeSchema,
    status: TaskStatusSchema.default("PLANNED"),
    plannedStart: z.string().default(""),
    deadline: z.string().default(""),
    plannedWorkDuration: z.coerce.number().min(0).default(0),
    plannedTravelDuration: z.coerce.number().min(0).default(0),
    plannedDepartureDuration: z.coerce.number().min(0).default(0),
    actualWorkDuration: z.coerce.number().min(0).nullable().default(null),
    actualTravelDuration: z.coerce.number().min(0).nullable().default(null),
    actualDepartureDuration: z.coerce.number().min(0).nullable().default(null),
  })
  .superRefine((data, ctx) => {
    if (data.taskType === "ERRANDS") {
      if (!data.deadline) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Pflichtfeld",
          path: ["deadline"],
        });
      }
    }

  });

export type TaskFormValues = z.infer<typeof taskFormSchema>;

export function getDefaultValues(taskType: TaskType): TaskFormValues {
  return {
    title: "",
    description: "",
    assignedTechnicianId: null,
    taskType,
    status: "PLANNED",
    plannedStart: "",
    deadline: "",
    plannedWorkDuration: 0,
    plannedTravelDuration: 0,
    plannedDepartureDuration: 0,
    actualWorkDuration: null,
    actualTravelDuration: null,
    actualDepartureDuration: null,
  };
}

function toIsoDate(dateStr: string): string {
  return new Date(dateStr).toISOString();
}

function toLocalDate(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toLocalDatetime(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

export function toCreateDto(values: TaskFormValues): CreateTaskDto {
  return {
    title: values.title,
    description: values.description || undefined,
    assignedTechnicianId: values.assignedTechnicianId ?? undefined,
    taskType: values.taskType,
    plannedStart: values.plannedStart ? toIsoDate(values.plannedStart) : undefined,
    deadline:
      values.taskType === "ERRANDS" && values.deadline
        ? toIsoDate(values.deadline)
        : undefined,
    plannedWorkDuration: values.plannedWorkDuration,
    plannedTravelDuration: values.plannedTravelDuration,
    plannedDepartureDuration: values.plannedDepartureDuration,
  };
}

export function toUpdateDto(values: TaskFormValues): UpdateTaskDto {
  return {
    title: values.title,
    description: values.description || null,
    assignedTechnicianId: values.assignedTechnicianId ?? undefined,
    taskType: values.taskType,
    status: values.status,
    plannedStart: values.plannedStart ? toIsoDate(values.plannedStart) : undefined,
    deadline:
      values.taskType === "ERRANDS" && values.deadline
        ? toIsoDate(values.deadline)
        : values.taskType === "GENERAL_TASK"
          ? null
          : undefined,
    plannedWorkDuration: values.plannedWorkDuration,
    plannedTravelDuration: values.plannedTravelDuration,
    plannedDepartureDuration: values.plannedDepartureDuration,
    actualWorkDuration: values.actualWorkDuration ?? undefined,
    actualTravelDuration: values.actualTravelDuration ?? undefined,
    actualDepartureDuration: values.actualDepartureDuration,
  };
}

export function fromTaskDetail(task: Task): TaskFormValues {
  return {
    title: task.title,
    description: task.description ?? "",
    assignedTechnicianId: task.assignedTechnicianId ?? null,
    taskType: task.taskType,
    status: task.status,
    plannedStart: task.plannedStart
      ? (new Date(task.plannedStart).getUTCHours() !== 0
          ? toLocalDatetime(task.plannedStart)
          : toLocalDate(task.plannedStart))
      : "",
    deadline: task.deadline ? toLocalDate(task.deadline) : "",
    plannedWorkDuration: task.plannedWorkDuration,
    plannedTravelDuration: task.plannedTravelDuration,
    plannedDepartureDuration: task.plannedDepartureDuration,
    actualWorkDuration: task.actualWorkDuration ?? null,
    actualTravelDuration: task.actualTravelDuration ?? null,
    actualDepartureDuration: task.actualDepartureDuration ?? null,
  };
}
